import React, { useState, useEffect } from 'react';

/**
 * @component LogoCarousel
 * @description Carrusel de logos de submarcas NFCores con transiciones suaves
 * @vibe-color 🔵 #3B82F6
 * @vibe-icon 🎠
 */

interface Logo {
  name: string;
  src: string;
  description: string;
}

const logos: Logo[] = [
  {
    name: 'SOS NFC',
    src: '/assets/images/SOS NFC-original.png',
    description: 'Pulseras de emergencia con tecnología NFC'
  },
  {
    name: 'NFC Pet',
    src: '/assets/images/NFC Pet.png',
    description: 'Identificación inteligente para mascotas'
  },
  {
    name: 'NFC ProCard',
    src: '/assets/images/NFC ProCard.png',
    description: 'Tarjetas de presentación digitales'
  },
  {
    name: 'NFC Social',
    src: '/assets/images/NFC Social.png',
    description: 'Conexión instantánea a redes sociales'
  },
  {
    name: 'ScanMenu',
    src: '/assets/images/ScanMenu.png',
    description: 'Menús digitales para restaurantes'
  }
];

interface LogoCarouselProps {
  autoPlay?: boolean;
  interval?: number;
  className?: string;
}

const LogoCarousel: React.FC<LogoCarouselProps> = ({ 
  autoPlay = true, 
  interval = 3000,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!autoPlay) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === logos.length - 1 ? 0 : prevIndex + 1
      );
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const currentLogo = logos[currentIndex];

  return (
    <div className={`flex flex-col items-center justify-center space-y-4 ${className}`}>
      {/* Logo principal */}
      <div className="relative h-24 w-32 flex items-center justify-center">
        <img
          src={currentLogo.src}
          alt={currentLogo.name}
          className="max-h-full max-w-full object-contain transition-all duration-500 ease-in-out"
          onError={(e) => {
            // Fallback si la imagen no existe
            e.currentTarget.src = '/logo.svg';
          }}
        />
      </div>

      {/* Nombre y descripción */}
      <div className="text-center space-y-1">
        <h3 className="text-lg font-semibold text-white">
          {currentLogo.name}
        </h3>
        <p className="text-sm text-white/80 max-w-xs">
          {currentLogo.description}
        </p>
      </div>

      {/* Indicadores */}
      <div className="flex space-x-2">
        {logos.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-white scale-125' 
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Ir al logo ${index + 1}`}
          />
        ))}
      </div>

      {/* Logos en miniatura para vista previa */}
      <div className="hidden lg:flex space-x-3 mt-4">
        {logos.map((logo, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-12 w-16 rounded-lg overflow-hidden transition-all duration-300 ${
              index === currentIndex 
                ? 'ring-2 ring-white scale-110' 
                : 'opacity-60 hover:opacity-80'
            }`}
          >
            <img
              src={logo.src}
              alt={logo.name}
              className="h-full w-full object-contain bg-white/10"
              onError={(e) => {
                e.currentTarget.src = '/logo.svg';
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default LogoCarousel;
